import { useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { useLang, PHONE_DISPLAY, PHONE_TEL } from "@/lib/i18n";

export default function Header() {
  const [open, setOpen] = useState(false);
  const { lang, setLang, t } = useLang();

  const NAV = [
    { href: "/#book", label: t("nav.book"), id: "book" },
    { href: "/#services", label: t("nav.services"), id: "services" },
    { href: "/#fleet", label: t("nav.fleet"), id: "fleet" },
    { href: "/destinations", label: t("nav.destinations"), id: "destinations" },
    { href: "/experiences", label: t("nav.experiences"), id: "experiences" },
    { href: "/#about", label: t("nav.about"), id: "about" },
    { href: "/#contact", label: t("nav.contact"), id: "contact" },
    { href: "/partners", label: t("nav.partner"), id: "partner" },
    { href: "/postani-vozac", label: t("nav.driver"), id: "driver" },
  ];

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/70 backdrop-blur-xl border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <a href="/" data-testid="logo-link" className="flex items-center gap-2 group">
          <span className="font-display text-2xl tracking-tight text-white">Juke</span>
          <span className="font-display text-2xl tracking-tight text-[#C5A059] italic">Transfers</span>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((l) => (
            <a
              key={l.id}
              href={l.href}
              data-testid={`nav-${l.id}-link`}
              className="text-sm text-zinc-400 hover:text-[#C5A059] transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          <LangToggle lang={lang} setLang={setLang} />
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="header-phone-link"
            className="flex items-center gap-2 text-sm text-zinc-300 hover:text-[#C5A059] transition-colors"
          >
            <Phone className="w-4 h-4" />
            <span>{PHONE_DISPLAY}</span>
          </a>
          <a
            href="/#book"
            data-testid="header-book-button"
            className="px-5 py-2.5 rounded-full bg-[#C5A059] hover:bg-[#D4AF37] text-black font-medium text-sm transition-all duration-300 hover:shadow-[0_0_24px_rgba(197,160,89,0.4)]"
          >
            {t("nav.quote")}
          </a>
        </div>

        <div className="flex items-center gap-3 lg:hidden">
          <LangToggle lang={lang} setLang={setLang} compact />
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="text-zinc-100 p-2"
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[#050505] border-t border-white/10">
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV.map((l) => (
              <a
                key={l.id}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-zinc-300 hover:text-[#C5A059] transition-colors"
              >
                {l.label}
              </a>
            ))}
            <a
              href={`tel:${PHONE_TEL}`}
              className="flex items-center gap-2 text-zinc-300"
            >
              <Phone className="w-4 h-4" /> {PHONE_DISPLAY}
            </a>
            <a
              href="/#book"
              onClick={() => setOpen(false)}
              className="mt-2 px-5 py-3 rounded-full bg-[#C5A059] text-black text-center font-medium"
            >
              {t("nav.quote")}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}

function LangToggle({ lang, setLang, compact = false }) {
  const items = ["en", "hr"];
  return (
    <div
      data-testid="lang-toggle"
      className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.03] p-0.5"
    >
      {items.map((code) => (
        <button
          key={code}
          data-testid={`lang-${code}`}
          onClick={() => setLang(code)}
          className={`text-[11px] font-medium uppercase tracking-[0.18em] px-3 ${compact ? "py-1" : "py-1.5"} rounded-full transition-colors ${
            lang === code
              ? "bg-[#C5A059] text-black"
              : "text-zinc-400 hover:text-zinc-100"
          }`}
        >
          {code}
        </button>
      ))}
    </div>
  );
}
